import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Search, HelpCircle, Star, Send, CheckCircle, ThumbsUp, ThumbsDown } from 'lucide-react';

const FAQFeedback = ({ config = {} }) => {
  const [activeTab, setActiveTab] = useState('faq');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [rating, setRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [helpfulFaqs, setHelpfulFaqs] = useState({});

  const [feedbackData, setFeedbackData] = useState({
    name: '',
    email: '',
    rating: 0,
    feedback: ''
  });

  // Merge defaults + admin config
  const defaultConfig = {
    title: "Frequently Asked Questions",
    subtitle: "Find answers to the most common questions about our products and services.",
    faqs: [
      // Your original FAQs as defaults
      {
        id: 1,
        category: 'shipping',
        question: 'What are your shipping options?',
        answer: 'We offer Standard Shipping (3-5 business days) for $10 and Express Shipping (1-2 business days) for $25...',
        helpful: 0
      },
      // ... add your other default FAQs here
    ],
    devMode: false,
    customStyles: {
      container: {
        padding: 'py-12 md:py-20',
        background: 'bg-white',
        titleColor: '#111827',
        subtitleColor: '#4B5563',
        questionColor: '#111827',
        answerColor: '#374151',
        borderColor: '#e5e7eb',
        hoverBg: '#f9fafb',
      },
    },
  };

  const finalConfig = { ...defaultConfig, ...config };
  const {
    title,
    subtitle,
    faqs,
    customStyles,
  } = finalConfig;

  const categories = [
    { value: 'all', label: 'All Questions' },
    { value: 'shipping', label: 'Shipping' },
    { value: 'returns', label: 'Returns' },
    { value: 'payment', label: 'Payment' },
    { value: 'account', label: 'Account' },
    { value: 'products', label: 'Products' }
  ];

  // Filter FAQs
  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleFaq = (id) => {
    setExpandedFaq(expandedFaq === id ? null : id);
  };

  const handleHelpful = (id, helpful) => {
    setHelpfulFaqs({ ...helpfulFaqs, [id]: helpful });
  };

  const handleFeedbackChange = (e) => {
    setFeedbackData({
      ...feedbackData,
      [e.target.name]: e.target.value
    });
  };

  const handleFeedbackSubmit = (e) => {
    e.preventDefault();
    setFeedbackSubmitted(true);
    setTimeout(() => {
      setFeedbackSubmitted(false);
      setFeedbackData({ name: '', email: '', rating: 0, feedback: '' });
      setRating(0);
    }, 3000);
  };

  // ── Smart container styling ────────────────────────────────────────────
  const containerClasses = [];
  const containerStyle = {};

  const bgValue = customStyles.container?.background || 'bg-white';

  if (bgValue.startsWith('bg-') || ['bg-white', 'bg-gray-50'].includes(bgValue)) {
    containerClasses.push(bgValue);
  } else {
    containerStyle.background = bgValue;
    if (bgValue.includes('url(')) {
      containerStyle.backgroundSize = 'cover';
      containerStyle.backgroundPosition = 'center';
      containerStyle.backgroundRepeat = 'no-repeat';
    }
  }

  if (customStyles.container?.padding) {
    containerClasses.push(customStyles.container.padding);
  }

  const finalContainerClass = containerClasses.join(' ');

  return (
    <div className={`min-h-screen ${finalContainerClass}`} style={containerStyle}>
      {/* Header */}
      <div className="border-b border-gray-200">
        <div className="max-w-4xl text-center mx-auto px-4 sm:px-6 py-8 md:py-12">
          <h1 
            className="text-3xl md:text-4xl font-bold mb-2"
            style={{ color: customStyles.container?.titleColor || '#111827' }}
          >
            {title}
          </h1>
         {/* <p 
            className="text-gray-600"
            style={{ color: customStyles.container?.subtitleColor || '#4B5563' }}
          >
            {subtitle}
          </p>*/}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        {activeTab === 'faq' && (
          <div>
            {/* Search & Filter */}
            <div className="mb-8">
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search FAQs..."
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              {searchQuery && (
                <p className="text-sm" style={{ color: customStyles.container?.subtitleColor || '#4B5563' }}>
                  Found {filteredFaqs.length} result{filteredFaqs.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>

            {/* FAQ List */}
            <div className="space-y-3">
              {filteredFaqs.map((faq) => (
                <div
                  key={faq.id}
                  className="bg-white rounded-xl border overflow-hidden transition-all"
                  style={{
                    borderColor: customStyles.container?.borderColor || '#e5e7eb',
                  }}
                >
                  <button
                    onClick={() => toggleFaq(faq.id)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                    style={{
                      backgroundColor: expandedFaq === faq.id ? (customStyles.container?.hoverBg || '#f9fafb') : 'transparent',
                    }}
                  >
                    <span 
                      className="font-semibold pr-4"
                      style={{ color: customStyles.container?.questionColor || '#111827' }}
                    >
                      {faq.question}
                    </span>
                    {expandedFaq === faq.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-600 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-600 flex-shrink-0" />
                    )}
                  </button>
                  
                  {expandedFaq === faq.id && (
                    <div className="px-6 pb-5 border-t" style={{ borderColor: customStyles.container?.borderColor || '#e5e7eb' }}>
                      <p 
                        className="leading-relaxed mb-4 pt-4"
                        style={{ color: customStyles.container?.answerColor || '#374151' }}
                      >
                        {faq.answer}
                      </p>
                      
                      {/* Was this helpful? */}
                      <div className="flex items-center gap-4 pt-4 border-t" style={{ borderColor: customStyles.container?.borderColor || '#e5e7eb' }}>
                        <span className="text-sm" style={{ color: customStyles.container?.subtitleColor || '#4B5563' }}>
                          Was this helpful?
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleHelpful(faq.id, true)}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                              helpfulFaqs[faq.id] === true
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            <ThumbsUp className="w-4 h-4" />
                            Yes
                          </button>
                          <button
                            onClick={() => handleHelpful(faq.id, false)}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                              helpfulFaqs[faq.id] === false
                                ? 'bg-red-100 text-red-700'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            <ThumbsDown className="w-4 h-4" />
                            No
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {filteredFaqs.length === 0 && (
                <div className="bg-white rounded-xl p-12 text-center">
                  <HelpCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2" style={{ color: customStyles.container?.titleColor || '#111827' }}>
                    No FAQs Found
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Try adjusting your search or filters
                  </p>
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('all');
                    }}
                    className="text-gray-900 font-medium hover:underline"
                  >
                    Clear filters
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FAQFeedback;